import type { ChipProps } from '@ui';
import type { FC } from 'react';
import type { AirportExtended, AirportRunway, AirportRunwayStatus } from '@/types/domain/core';
import { Chip, Flex, Icon, Section, Typography } from '@ui';

import { useTranslation } from 'react-i18next';
import styles from './AirportRunways.module.css';

type Props = {
    airport: AirportExtended;
};

type Subcomponents = {
    Skeleton: FC;
};

const SPLITTED_DIRECTIONS = ['dep', 'arr'];
const RUNWAY_STATUS_CHIP_MAP: Record<AirportRunwayStatus, ChipProps['variant']> = {
    active: 'active',
    closed: 'danger',
    unknown: 'default'
};

const AirportRunways: FC<Props> & Subcomponents = ({ airport }) => {
    const splitByDirection = airport.runways.every(({ direction }) => SPLITTED_DIRECTIONS.includes(direction));
    const { t } = useTranslation();

    const renderRunway = ({ id, code, status }: AirportRunway) => (
        <Chip key={id} variant={RUNWAY_STATUS_CHIP_MAP[status]}>{code}</Chip>
    );

    const runwaysContent = splitByDirection
        ? (
                <Flex vertical gap={16}>
                    <Flex align="center" justify="space-between">
                        <Typography.Paragraph color="textPrimary">{t('Airports_Card_Departure')}</Typography.Paragraph>
                        <Section variant="outlined">
                            <Flex gap={16}>
                                {airport.runways.filter(({ direction }) => direction === 'dep').map(renderRunway)}
                            </Flex>
                        </Section>
                    </Flex>
                    <Flex align="center" justify="space-between">
                        <Typography.Paragraph color="textPrimary">{t('Airports_Card_Arrival')}</Typography.Paragraph>
                        <Section variant="outlined">
                            <Flex gap={16}>
                                {airport.runways.filter(({ direction }) => direction === 'arr').map(renderRunway)}
                            </Flex>
                        </Section>
                    </Flex>
                </Flex>
            )
        : (
                <Section variant="outlined">
                    <Flex gap={16}>
                        {airport.runways.map(renderRunway)}
                    </Flex>
                </Section>
            );

    return (
        <Flex vertical gap={24} className={styles.container}>
            <Typography.Heading level={4} color="textSecondary">{t('Airports_Card_Runways')}</Typography.Heading>
            {airport.runways.length
                ? runwaysContent
                : (
                        <Flex vertical align="center" justify="center" gap={16} className={styles.emptyContainer}>
                            <Icon.NoLuggage size={48} color="textTertiary" />
                            <Typography.Paragraph level={2} align="center" color="textSecondary">{t('Airports_Card_Runways_Empty')}</Typography.Paragraph>
                        </Flex>
                    )}
        </Flex>
    );
};

AirportRunways.Skeleton = () => {
    const { t } = useTranslation();
    return (
        <Flex vertical gap={24} className={styles.container}>
            <Typography.Heading level={4} color="textSecondary">{t('Airports_Card_Runways')}</Typography.Heading>
            <Section variant="outlined">
                <Flex gap={16}>
                    <Chip.Skeleton />
                    <Chip.Skeleton />
                </Flex>
            </Section>
        </Flex>
    );
};

export { AirportRunways };
